/**
 * Página de gestión de Merchants del Backoffice Operativo.
 * Listado con filtros, vista 360 en drawer y edición.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE
 */

import { useCallback, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button, Eyebrow } from '@flexibilitypay/ui';

import {
  MerchantFilters,
  MerchantListTable,
  MerchantDrawer360,
  MerchantEditForm,
  useMerchants,
  useExportMerchantsCsv,
} from '../features/merchants';
import type { Merchant, MerchantStatus } from '../features/merchants';

const PAGE_SIZE = 10;

export function MerchantsPage() {
  const { t } = useTranslation(['merchants', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado de UI
  const [selectedMerchantFor360, setSelectedMerchantFor360] = useState<string | null>(null);
  const [merchantToEdit, setMerchantToEdit] = useState<Merchant | null>(null);

  // Extraer parámetros de URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const search = searchParams.get('search') || '';
  const statusFilter = (searchParams.get('estado') as MerchantStatus) || undefined;

  // Query de merchants
  const {
    data: merchantsData,
    isLoading,
    error,
    refetch,
  } = useMerchants({
    page,
    size: PAGE_SIZE,
    search: search || undefined,
    estado: statusFilter,
  });

  // Mutation de export
  const exportMutation = useExportMerchantsCsv();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSearch = useCallback(
    (newSearch: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (newSearch) {
          params.set('search', newSearch);
        } else {
          params.delete('search');
        }
        params.set('page', '0'); // Reset a primera página
        return params;
      });
    },
    [setSearchParams],
  );

  const handleStatusFilter = useCallback(
    (status: MerchantStatus | undefined) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (status) {
          params.set('estado', status);
        } else {
          params.delete('estado');
        }
        params.set('page', '0'); // Reset a primera página
        return params;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', String(newPage));
        return params;
      });
    },
    [setSearchParams],
  );

  const handleExport = useCallback(() => {
    exportMutation.mutate({
      estado: statusFilter,
      search: search || undefined,
    });
  }, [exportMutation, statusFilter, search]);

  const handleView360 = useCallback((merchant: Merchant) => {
    setSelectedMerchantFor360(merchant.id);
  }, []);

  const handleEdit = useCallback((merchant: Merchant) => {
    setMerchantToEdit(merchant);
  }, []);

  const handleRowClick = useCallback((merchant: Merchant) => {
    // Navegar al detalle con tabs
    navigate(`/merchants/${merchant.id}`);
  }, [navigate]);

  const handleViewHistory = useCallback(() => {
    if (selectedMerchantFor360) {
      // Navegar al historial de movimientos filtrado por merchant
      navigate(`/movements?merchantId=${selectedMerchantFor360}`);
    }
  }, [navigate, selectedMerchantFor360]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="container py-6">
      {/* Header */}
      <div className="mb-6">
        <Eyebrow>{t('merchants:breadcrumb', 'Backoffice Operativo')}</Eyebrow>
        <h1 className="text-2xl font-bold">{t('merchants:title', 'Merchants')}</h1>
        <p className="text-muted-foreground">
          {t('merchants:subtitle', 'Gestión de clientes del PSP')}
        </p>
      </div>

      {/* Filtros */}
      <MerchantFilters
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onExport={handleExport}
        isExporting={exportMutation.isPending}
        currentSearch={search}
        currentStatus={statusFilter}
      />

      {/* Error state */}
      {error && (
        <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">
              {t('merchants:errors.loadFailed', 'Error al cargar merchants')}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
            {t('common:retry', 'Reintentar')}
          </Button>
        </div>
      )}

      {/* Tabla */}
      <MerchantListTable
        merchants={merchantsData?.content || []}
        isLoading={isLoading}
        onView360={handleView360}
        onEdit={handleEdit}
        onRowClick={handleRowClick}
        currentPage={page}
        totalPages={merchantsData?.totalPages || 0}
        onPageChange={handlePageChange}
      />

      {/* Total de resultados */}
      {merchantsData && (
        <p className="mt-4 text-sm text-muted-foreground">
          {t('merchants:list.total', 'Mostrando {{count}} de {{total}} merchants', {
            count: merchantsData.content.length,
            total: merchantsData.totalElements,
          })}
        </p>
      )}

      {/* Drawer Vista 360 */}
      <MerchantDrawer360
        merchantId={selectedMerchantFor360}
        open={selectedMerchantFor360 !== null}
        onClose={() => setSelectedMerchantFor360(null)}
        onViewHistory={handleViewHistory}
      />

      {/* Modal Edición */}
      {merchantToEdit && (
        <MerchantEditForm
          merchant={merchantToEdit}
          open={merchantToEdit !== null}
          onClose={() => setMerchantToEdit(null)}
          onSuccess={() => {
            // Toast de éxito se maneja en el hook
          }}
          canChangeStatus={true} // TODO: Obtener de contexto de auth
        />
      )}
    </div>
  );
}
